import React, { useState } from 'react'
import { useAccount, useConnect, useDisconnect, useSignMessage } from 'wagmi'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '../context/AuthContext'
import { useTenant } from '../context/TenantContext'
import { authAPI } from '../lib/api'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog'
import { Wallet, Mail, Loader2, X } from 'lucide-react'
import { toast } from 'sonner'

export function AuthModal({ open, onOpenChange }) {
  const { t } = useTranslation()
  const [tab, setTab] = useState('login')
  const [loading, setLoading] = useState(false)
  const [walletLoading, setWalletLoading] = useState(false)
  const { login, register, loginWithWallet } = useAuth()
  const { tenant } = useTenant()
  
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState('investor')
  
  const { address, isConnected } = useAccount()
  const { connect, connectors } = useConnect()
  const { disconnect } = useDisconnect()
  const { signMessageAsync } = useSignMessage()

  const handleEmailAuth = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      if (tab === 'login') {
        await login(email, password)
        toast.success('Welcome back!')
      } else {
        await register(email, password, name, role)
        toast.success('Account created successfully!')
      }
      onOpenChange(false)
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed')
    } finally {
      setLoading(false)
    }
  }

  const handleWalletConnect = async () => {
    const injectedConnector = connectors.find(c => c.id === 'injected' || c.type === 'injected' || c.name?.toLowerCase().includes('metamask'))
    if (injectedConnector) {
      try {
        await connect({ connector: injectedConnector })
      } catch (error) {
        console.error('Wallet connect error:', error)
        toast.error('Failed to connect wallet. Make sure MetaMask is installed.')
      }
    } else {
      toast.error('No MetaMask detected. Please install MetaMask.')
    }
  }

  const handleWalletAuth = async () => {
    if (!address) return
    setWalletLoading(true)
    try {
      const challengeRes = await authAPI.getWalletChallenge(address, 1)
      const challenge = challengeRes.data.challenge
      const signature = await signMessageAsync({ message: challenge })
      await loginWithWallet(address, signature, challenge)
      toast.success('Wallet connected successfully!')
      onOpenChange(false)
    } catch (error) {
      console.error('Wallet auth error:', error)
      toast.error(error.response?.data?.detail || 'Wallet authentication failed')
    } finally {
      setWalletLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px] bg-white border-slate-200 p-0 overflow-hidden rounded-none" data-testid="auth-modal">
        <DialogHeader className="p-6 pb-4 bg-brand-secondary">
          <div className="flex items-center gap-3 mb-2">
            <img 
              src={tenant?.branding?.logo_url || "https://uni2ecosystem.com/images/logos/tokenizelogo.png"} 
              alt={tenant?.name || "2kenize"} 
              className="h-8 w-auto"
            />
          </div>
          <DialogTitle className="text-2xl font-heading text-white">
            {tab === 'login' ? t('auth.welcomeBack') : t('auth.createAccount')}
          </DialogTitle>
          <p className="text-white/60 text-sm font-body">
            {tab === 'login' ? t('auth.signInSubtitle') : t('auth.registerSubtitle')}
          </p>
        </DialogHeader>

        <div className="px-6 pb-6">
          <div className="my-6">
            <AnimatePresence mode="wait">
              {!isConnected ? (
                <motion.div key="connect" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                  <Button
                    onClick={handleWalletConnect}
                    className="w-full h-14 bg-brand-secondary hover:bg-brand-secondary/90 text-white font-semibold tracking-wide rounded-none"
                    data-testid="connect-wallet-btn"
                  >
                    <Wallet className="w-5 h-5 mr-2" />
                    {t('auth.connectMetaMask')}
                  </Button>
                </motion.div>
              ) : (
                <motion.div key="sign" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-[#F8FAFC] border border-slate-200">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                      <span className="text-sm text-slate-600 font-mono">
                        {address?.slice(0, 6)}...{address?.slice(-4)}
                      </span>
                    </div>
                    <button onClick={() => disconnect()} className="text-slate-400 hover:text-slate-600">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <Button
                    onClick={handleWalletAuth}
                    disabled={walletLoading}
                    className="w-full h-14 bg-brand-primary hover:bg-brand-primary-hover text-brand-secondary font-semibold tracking-wide rounded-none"
                    data-testid="sign-wallet-btn"
                  >
                    {walletLoading ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" />{t('auth.signing')}</>
                    ) : (
                      t('auth.signContinue')
                    )}
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="relative flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-slate-500 uppercase tracking-widest">{t('auth.orEmail')}</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="w-full grid grid-cols-2 bg-[#F8FAFC] p-1 rounded-none">
              <TabsTrigger value="login" className="data-[state=active]:bg-white data-[state=active]:text-brand-secondary text-slate-500 rounded-none font-semibold" data-testid="login-tab">
                {t('auth.signIn')}
              </TabsTrigger>
              <TabsTrigger value="register" className="data-[state=active]:bg-white data-[state=active]:text-brand-secondary text-slate-500 rounded-none font-semibold" data-testid="register-tab">
                {t('auth.register')}
              </TabsTrigger>
            </TabsList>

            <form onSubmit={handleEmailAuth} className="mt-4 space-y-4">
              <TabsContent value="register" className="space-y-4 mt-0">
                <div className="space-y-2">
                  <Label className="text-slate-600 text-sm font-semibold">{t('auth.fullName')}</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="John Doe" required
                    className="h-12 bg-white border-slate-200 focus:border-brand-primary text-brand-secondary placeholder:text-slate-400 rounded-none" data-testid="register-name-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600 text-sm font-semibold">{t('auth.accountType')}</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" onClick={() => setRole('investor')}
                      className={`p-3 border text-sm font-semibold transition-all ${role === 'investor' ? 'border-brand-primary bg-brand-primary/10 text-brand-secondary' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
                      data-testid="role-investor-btn">
                      {t('auth.investor')}
                    </button>
                    <button type="button" onClick={() => setRole('issuer')}
                      className={`p-3 border text-sm font-semibold transition-all ${role === 'issuer' ? 'border-brand-primary bg-brand-primary/10 text-brand-secondary' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
                      data-testid="role-issuer-btn">
                      {t('auth.issuer')}
                    </button>
                  </div>
                </div>
              </TabsContent>

              <div className="space-y-2">
                <Label className="text-slate-600 text-sm font-semibold">{t('auth.email')}</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required
                  className="h-12 bg-white border-slate-200 focus:border-brand-primary text-brand-secondary placeholder:text-slate-400 rounded-none" data-testid="email-input" />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-600 text-sm font-semibold">{t('auth.password')}</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required
                  className="h-12 bg-white border-slate-200 focus:border-brand-primary text-brand-secondary placeholder:text-slate-400 rounded-none" data-testid="password-input" />
              </div>

              <Button type="submit" disabled={loading}
                className="w-full h-14 bg-brand-primary hover:bg-brand-primary-hover text-brand-secondary font-semibold tracking-wide rounded-none" data-testid="submit-auth-btn">
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <><Mail className="w-4 h-4 mr-2" />{tab === 'login' ? t('auth.signIn') : t('auth.createAccount')}</>
                )}
              </Button>
            </form>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  )
}
